import type { FC, ReactNode } from 'react';
import { useMemo } from 'react';
import { Card, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import IconPeople from '~icons/eva/people-fill';
import IconCreditCard from '~icons/eva/credit-card-fill';
import IconCreditCardOutline from '~icons/eva/credit-card-outline';
import IconWarning from '~icons/eva/alert-triangle-fill';
import IconOver from '~icons/eva/alert-circle-fill';

import {
  getIncompletePliantTransactionsParams,
  useBudgets,
  useGetNursingHomeEmployees,
  usePliantCardCount,
  usePliantTransactions,
} from '@pflegenavi/frontend/api-nursing-home';
import { budgetStatus, isBudgetExceeded } from '@pflegenavi/shared/frontend';
import {
  useEmployeeIsNursingHomeAdminOrManager,
  useNursingHomeContext,
} from '@pflegenavi/frontend/nursing-home-context';
import { useFeatureFlagEnabled } from '@pflegenavi/frontend/feature-flags';
import { EmployeeState, FeatureFlag } from '@pflegenavi/shared/api';
import { nhAppMainPages } from '@pflegenavi/frontend/routing';
import { DashboardStatCard } from './DashboardStatCard';

export const DashboardStatBar: FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { selectedNursingHome } = useNursingHomeContext();
  const nursingHomeId = selectedNursingHome?.id;

  const isAdminOrManager = useEmployeeIsNursingHomeAdminOrManager();
  const expenseManagementEnabled = useFeatureFlagEnabled(
    FeatureFlag.ExpenseManagement
  );

  // The bar renders nothing without the flag, and its first two stats are
  // admin/manager-only. Passing no id disables those queries (both hooks are
  // enabled on !!nursingHomeId), so nobody fetches what they cannot see.
  const adminStatsId =
    expenseManagementEnabled && isAdminOrManager ? nursingHomeId : undefined;
  const adminStatsLegalEntityId =
    expenseManagementEnabled && isAdminOrManager
      ? selectedNursingHome?.legalEntityId
      : undefined;

  const { data: employees } = useGetNursingHomeEmployees({
    nursingHomeId: adminStatsId,
    paginationParams: { page: 1, page_size: 1 },
    employeeStates: [EmployeeState.Active],
  });

  const cardCount = usePliantCardCount(adminStatsLegalEntityId, adminStatsId);

  const { data: pliantTransactions } = usePliantTransactions(
    getIncompletePliantTransactionsParams(
      1,
      1,
      {
        legalEntityId: selectedNursingHome?.legalEntityId,
        nursingHomeId: selectedNursingHome?.id,
      },
      undefined
    ),
    {
      enabled: expenseManagementEnabled && !!selectedNursingHome?.legalEntityId,
    }
  );

  const { data: budgetData } = useBudgets(
    { nursingHomeId },
    { enabled: expenseManagementEnabled && !!nursingHomeId }
  );

  const budgetCounts = useMemo(() => {
    const budgets = budgetData?.data ?? [];
    return {
      warning: budgets.filter((b) => budgetStatus(b) !== 'ok').length,
      over: budgets.filter(isBudgetExceeded).length,
    };
  }, [budgetData?.data]);

  if (!expenseManagementEnabled) {
    return null;
  }

  const items: Array<{
    dataCy: string;
    title: string;
    subtitle: string;
    value: number;
    color: string;
    icon: ReactNode;
    onClick?: () => void;
  }> = [];

  // Active-employee count is admin/manager-only (query disabled otherwise).
  if (isAdminOrManager) {
    items.push({
      dataCy: 'stat-active-employees',
      title: t('dashboard.stat.active-employees'),
      subtitle: t('dashboard.stat.active-employees.subtitle'),
      value: employees?.meta?.total_count ?? 0,
      color: theme.palette.text.primary,
      icon: <IconPeople />,
      onClick: () => navigate(nhAppMainPages.NURSING_HOME_EMPLOYEES_LIST),
    });

    // No onClick: NH Web has no manager-facing card list to navigate to.
    items.push({
      dataCy: 'stat-pliant-cards',
      title: t('dashboard.stat.pliant-cards'),
      subtitle: t('dashboard.stat.pliant-cards.subtitle', {
        physical: cardCount.physical,
        virtual: cardCount.virtual,
      }),
      value: cardCount.total,
      color: theme.palette.success.main,
      icon: <IconCreditCardOutline />,
    });
  }

  items.push({
    dataCy: 'stat-open-card-payments',
    title: t('dashboard.stat.open-card-payments'),
    subtitle: t('dashboard.stat.open-card-payments.subtitle'),
    value: pliantTransactions?.meta?.total_count ?? 0,
    color: theme.palette.info.main,
    icon: <IconCreditCard />,
    onClick: () =>
      navigate(nhAppMainPages.TRANSACTIONS_UNASSOCIATED_CARD_PAYMENTS),
  });

  items.push({
    dataCy: 'stat-budgets-warning',
    title: t('dashboard.stat.budgets-warning'),
    subtitle: t('dashboard.stat.budgets-warning.subtitle'),
    value: budgetCounts.warning,
    color: theme.palette.warning.main,
    icon: <IconWarning />,
    onClick: () => navigate(nhAppMainPages.BUDGETS),
  });

  items.push({
    dataCy: 'stat-budgets-over',
    title: t('dashboard.stat.budgets-over'),
    subtitle: t('dashboard.stat.budgets-over.subtitle'),
    value: budgetCounts.over,
    color: theme.palette.error.main,
    icon: <IconOver />,
    onClick: () => navigate(nhAppMainPages.BUDGETS),
  });

  return (
    <Card sx={{ display: 'flex', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
      {items.map((item, i) => (
        <DashboardStatCard key={item.dataCy} {...item} first={i === 0} />
      ))}
    </Card>
  );
};
